import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackLead } from '@/lib/metaPixel';
import { linkSignup } from '@/lib/growthTracking';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem('session_token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const claimPendingGuestEvent = async (token) => {
    // If the user came here from a guest-created schedule (see
    // AnonSignupPromoModal), attach that event to their new account so it
    // appears in /my-schedules. No-op when there's nothing pending.
    if (typeof window === 'undefined') return null;
    let pendingId = null;
    try { pendingId = localStorage.getItem('sg_guest_event_id'); } catch { /* private mode */ }
    if (!pendingId) return null;
    try {
      const authToken = token || localStorage.getItem('session_token');
      if (!authToken) return null;
      const res = await fetch(`${API_URL}/api/events/${pendingId}/claim`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (res.ok) {
        try { localStorage.removeItem('sg_guest_event_id'); } catch { /* ignore */ }
        return pendingId;
      }
      // 403 => somebody else's event; drop the stale flag either way so we
      // don't retry every login.
      if (res.status === 403 || res.status === 404) {
        try { localStorage.removeItem('sg_guest_event_id'); } catch { /* ignore */ }
      }
      return null;
    } catch {
      return null;
    }
  };

  const login = async (email, password) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = async function() {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            localStorage.setItem('session_token', data.session_token);
            setUser(data.user);
            // Attach any guest schedule they created before signing in.
            const claimed = await claimPendingGuestEvent(data.session_token);
            checkAuth();
            resolve({ ...data, claimed_event_id: claimed });
          } else {
            reject(new Error(data.detail || 'Invalid email or password'));
          }
        } catch {
          reject(new Error('Server error. Please try again.'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error. Please check your connection.'));
      };
      
      xhr.send(JSON.stringify({ email, password }));
    });
  };

  const register = async (email, password, name) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/register`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = async function() {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            localStorage.setItem('session_token', data.session_token);
            setUser(data.user);
            // Meta Pixel: fire Lead for every new email+password signup
            // (this endpoint always creates a brand-new user — the backend
            // 409s on existing email). Facebook uses Lead as the top-of-
            // funnel conversion signal for campaign optimization.
            trackLead('email');
            linkSignup(data.session_token);
            // Attach any guest schedule they created before signing up.
            const claimed = await claimPendingGuestEvent(data.session_token);
            resolve({ ...data, claimed_event_id: claimed });
          } else {
            reject(new Error(data.detail || 'Registration failed'));
          }
        } catch {
          reject(new Error('Server error. Please try again.'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error. Please check your connection.'));
      };
      
      xhr.send(JSON.stringify({ email, password, name }));
    });
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  const applyCoupon = async (code) => {
    const token = localStorage.getItem('session_token');
    const response = await fetch(`${API_URL}/api/subscription/apply-coupon`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify({ code })
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Failed to apply coupon');
    }

    // Instant local update: the backend returns the refreshed user doc so
    // the PRO badge / Pro-gated UI can react before /auth/me round-trips.
    if (data.user) {
      setUser(data.user);
    }
    // Belt-and-suspenders — re-fetch /auth/me in the background so any
    // server-side derived flags (days_until_downgrade, expiry_warning)
    // that aren't in the raw user doc also flow through.
    await checkAuth();
    return data;
  };

  const value = {
    user,
    setUser,
    loading,
    isLoading: loading,
    login,
    register,
    logout,
    applyCoupon,
    checkAuth,
    claimPendingGuestEvent,
    refreshUser: checkAuth,
    isAuthenticated: !!user,
    isPro: user?.subscription?.startsWith('pro_') && (!user?.subscription_status || ['active', 'grace_period'].includes(user?.subscription_status)),
    isAdmin: user?.email === 'collin@mbscayman.com',
    subscriptionStatus: user?.subscription_status || 'active',
    isInGracePeriod: user?.subscription_status === 'grace_period',
    daysUntilDowngrade: user?.days_until_downgrade
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
